import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import { Link } from 'react-router-dom';
import { Calculator, ArrowRight, Moon, Sun, Tractor } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import { getCurrentMonthYear } from '../utils/currentDate';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import { formatNumber } from '../utils/timeCalculations';

const AdicionalNoturnoValorPage = () => {
  const [salario, setSalario] = useState('');
  const [jornadaSemanal, setJornadaSemanal] = useState('220');
  const [horasNoturnas, setHorasNoturnas] = useState('');
  const [percentual, setPercentual] = useState('20');
  const [incluirHoraExtra, setIncluirHoraExtra] = useState(false);

  const resultado = useMemo(() => {
    const sal = parseFloat(salario);
    const divisor = parseFloat(jornadaSemanal);
    const horas = parseFloat(horasNoturnas);
    const perc = parseFloat(percentual);

    if (!sal || !horas || sal <= 0 || horas <= 0) {
      return null;
    }

    const horaNormal = sal / divisor;
    const adicionalPorHora = horaNormal * (perc / 100);
    const horaNoturna = horaNormal + adicionalPorHora;
    
    let valorFinal = horaNoturna;
    if (incluirHoraExtra) {
      valorFinal = horaNoturna * 1.5; // 50% de hora extra
    }
    
    // Hora noturna reduzida (Art. 73, §1º CLT): urban workers count 52min30s as
    // one hour, so each clock hour is worth 60/52.5 computed hours. Rural workers
    // (the 25% option) have no reduced hour (Lei 5.889/73).
    const rural = perc === 25;
    const horasComputadas = rural ? horas : horas * (60 / 52.5);
    const totalAdicional = adicionalPorHora * horasComputadas;

    return {
      horaNormal: horaNormal.toFixed(2),
      adicionalPorHora: adicionalPorHora.toFixed(2),
      horaNoturna: horaNoturna.toFixed(2),
      valorFinal: valorFinal.toFixed(2),
      totalAdicional: totalAdicional.toFixed(2),
      horasComputadas: formatNumber(horasComputadas),
      rural,
      percentual: perc,
      quantidade: horas
    };
  }, [salario, jornadaSemanal, horasNoturnas, percentual, incluirHoraExtra]);

  const faqs = [
    {
      question: 'Qual é o valor do adicional noturno?',
      answer: 'O adicional noturno para trabalhadores urbanos é de, no mínimo, 20% sobre o valor da hora normal, conforme o artigo 73 da CLT. Para rurais, o mínimo é de 25%. Convenções coletivas podem estabelecer percentuais maiores.'
    },
    {
      question: 'A partir de que horas começa o adicional noturno?',
      answer: 'Para trabalhadores urbanos, o período noturno vai das 22h às 5h do dia seguinte. Para rurais na lavoura, das 21h às 5h. Na pecuária, das 20h às 4h.'
    },
    {
      question: 'Adicional noturno é obrigatório?',
      answer: 'Sim. É um direito constitucional garantido pelo artigo 7º da Constituição Federal. Todo trabalhador com carteira assinada que trabalhe no período noturno deve recebê-lo.'
    },
    {
      question: 'Como calcular adicional noturno sobre o salário mínimo?',
      answer: 'Com o salário mínimo de R$ 1.621 em 2026: divida por 220 (= R$ 7,37 por hora). O adicional de 20% é R$ 1,47 por hora noturna. Trabalhando 44 horas noturnas de relógio no mês (50,29 horas computadas, por causa da hora noturna reduzida), o adicional total é de aproximadamente R$ 74,10.'
    },
    {
      question: 'Quem trabalha das 18h às 22h recebe adicional noturno?',
      answer: 'Não. O período noturno urbano começa às 22h. Quem termina o turno às 22h não trabalha no período noturno e, portanto, não tem direito ao adicional.'
    },
    {
      question: 'Adicional noturno entra no cálculo de férias?',
      answer: 'Sim, quando pago com habitualidade. O adicional noturno integra a base de cálculo de férias, 13º salário, FGTS, aviso prévio e DSR, conforme a Súmula 60 do TST.'
    },
    {
      question: 'Hora noturna reduzida — o que é?',
      answer: 'A hora noturna para trabalhadores urbanos equivale a 52 minutos e 30 segundos (e não 60 minutos). Na prática, quem trabalha 7 horas noturnas recebe como se tivesse trabalhado 8 horas. Essa regra não se aplica a trabalhadores rurais.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Adicional Noturno: Valor, Cálculo e Tabela 2026</title>
        <meta name="description" content="Descubra o valor do adicional noturno em 2026. Calculadora grátis + tabela de valores, percentual de 20%, hora noturna reduzida e regras da CLT." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
        <link rel="canonical" href="https://calculadoradehoras.net.br/adicional-noturno-valor" />
        
        <meta property="og:title" content="Adicional Noturno: Valor, Cálculo e Tabela 2026" />
        <meta property="og:description" content="Descubra o valor do adicional noturno em 2026. Calculadora grátis + tabela de valores, percentual de 20%, hora noturna reduzida e regras da CLT." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/adicional-noturno-valor" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Adicional Noturno: Valor, Cálculo e Tabela 2026" />
        <meta name="twitter:description" content="Descubra o valor do adicional noturno em 2026. Calculadora grátis + tabela de valores, percentual de 20%, hora noturna reduzida e regras da CLT." />
        
        <link rel="alternate" hrefLang="pt-BR" href="https://calculadoradehoras.net.br/adicional-noturno-valor" />
        <link rel="alternate" hrefLang="x-default" href="https://calculadoradehoras.net.br/adicional-noturno-valor" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Valor do Adicional Noturno","item":"https://calculadoradehoras.net.br/adicional-noturno-valor"}
        ]
      }} />

      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4" aria-label="Breadcrumb">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Valor do Adicional Noturno</span>
        </nav>
        {/* Header */}
        <div className="text-center mb-4">
          <div className="inline-block bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Mínimo de 20% sobre a hora normal
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-dark-text mb-4">
            Quanto é o Adicional Noturno?
          </h1>
          <p className="text-lg text-body-text mb-2">
            O adicional noturno para trabalhadores urbanos é de, no mínimo, <strong>20% sobre o valor da hora diurna</strong>, conforme o artigo 73 da CLT. Para trabalhadores rurais, o percentual mínimo é de <strong>25%</strong>. O período noturno urbano vai das <strong>22h às 5h</strong>.
          </p>
          <p className="text-sm text-body-text mt-2" data-testid="last-updated" suppressHydrationWarning>{`Última atualização: ${getCurrentMonthYear()}`}</p>
        </div>

        {/* Calculator */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8" id="calculadora">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-6">
            Calculadora de Adicional Noturno
          </h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Salário Bruto Mensal (R$)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={salario}
                onChange={(e) => setSalario(e.target.value)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-lg focus:outline-none focus:border-blue-primary"
                placeholder="2500.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Jornada Semanal
              </label>
              <select
                value={jornadaSemanal}
                onChange={(e) => setJornadaSemanal(e.target.value)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg focus:outline-none focus:border-blue-primary"
              >
                <option value="220">44 horas (÷ 220)</option>
                <option value="180">36 horas (÷ 180)</option>
                <option value="150">30 horas (÷ 150)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Horas Noturnas Trabalhadas
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={horasNoturnas}
                onChange={(e) => setHorasNoturnas(e.target.value)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg text-lg focus:outline-none focus:border-blue-primary"
                placeholder="44"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Percentual do Adicional
              </label>
              <select
                value={percentual}
                onChange={(e) => setPercentual(e.target.value)}
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg focus:outline-none focus:border-blue-primary"
              >
                <option value="20">20% — Urbano (CLT)</option>
                <option value="25">25% — Rural</option>
                <option value="30">30% — Convenção coletiva</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="horaExtra"
                checked={incluirHoraExtra}
                onChange={(e) => setIncluirHoraExtra(e.target.checked)}
                className="w-5 h-5 text-blue-primary border-border-gray rounded focus:ring-blue-primary"
              />
              <label htmlFor="horaExtra" className="ml-3 text-base text-dark-text">
                Incluir hora extra noturna (+50%)
              </label>
            </div>
          </div>

          {resultado && (
            <div className="mt-8 bg-blue-50 rounded-xl p-6">
              <div className="text-center mb-6">
                <p className="text-sm text-body-text mb-2">Valor do Adicional Noturno a Receber</p>
                <p className="text-5xl font-bold text-blue-primary">R$ {formatNumber(resultado.totalAdicional)}</p>
                <p className="text-sm text-body-text mt-2">
                  {resultado.rural
                    ? `${formatNumber(resultado.quantidade)}h noturnas (trabalhador rural: sem hora reduzida)`
                    : `${formatNumber(resultado.quantidade)}h de relógio = ${resultado.horasComputadas}h computadas (hora noturna reduzida de 52min30s)`}
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-border-gray">
                <div>
                  <p className="text-sm text-body-text mb-1">Hora Normal</p>
                  <p className="text-xl font-bold text-dark-text">R$ {formatNumber(resultado.horaNormal)}</p>
                </div>
                <div>
                  <p className="text-sm text-body-text mb-1">Hora Noturna</p>
                  <p className="text-xl font-bold text-dark-text">R$ {formatNumber(resultado.horaNoturna)}</p>
                </div>
                <div>
                  <p className="text-sm text-body-text mb-1">Adicional / Hora</p>
                  <p className="text-xl font-bold text-dark-text">R$ {formatNumber(resultado.adicionalPorHora)}</p>
                </div>
                <div>
                  <p className="text-sm text-body-text mb-1">
                    {incluirHoraExtra ? 'Hora Extra Noturna' : 'Horas Computadas'}
                  </p>
                  <p className="text-xl font-bold text-dark-text">
                    {incluirHoraExtra ? `R$ ${formatNumber(resultado.valorFinal)}` : `${resultado.horasComputadas}h`}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CTA to Calculator Page */}
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6 md:p-8 mb-8 border-2 border-purple-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Calculator className="text-purple-600" size={32} />
                <p className="font-bold text-xl font-semibold text-dark-text">
                  Precisa calcular com mais detalhes?
                </p>
              </div>
              <p className="text-base text-body-text">
                Use nossa <strong>calculadora de adicional noturno completa</strong> a partir do valor da hora, com a hora noturna reduzida e a comparação com o valor diurno.
              </p>
            </div>
            <Link
              to="/calculadora-de-adicional-noturno"
              className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Usar Calculadora Completa
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>

        <AdSlot />

        {/* Content Sections */}
        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            O Que é o Adicional Noturno?
          </h2>
          <p className="text-base text-body-text mb-4">
            O <strong>adicional noturno</strong> é um valor extra que o trabalhador recebe por exercer suas atividades no período da noite. A legislação brasileira reconhece que o trabalho noturno é mais desgastante e, por isso, garante uma compensação financeira obrigatória.
          </p>
          <p className="text-base text-body-text mb-4">
            Esse direito está previsto no <strong>artigo 73 da CLT</strong> e no <strong>artigo 7º, inciso IX da Constituição Federal</strong>. Todo empregado que trabalha no período noturno tem direito ao adicional, independente da profissão ou do setor de atividade.
          </p>
          <p className="text-base text-body-text">
            O valor do adicional noturno é calculado como um percentual sobre a hora normal de trabalho. Para trabalhadores urbanos, o mínimo é de 20%. Para rurais, 25%. Convenções coletivas podem determinar percentuais maiores.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Adicional Noturno: A Partir de Que Horas?
          </h2>
          <p className="text-base text-body-text mb-6">
            O horário do adicional noturno varia conforme a categoria do trabalhador. Veja os horários definidos pela legislação.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-50 rounded-lg p-6 border-2 border-blue-200">
              <div className="flex items-center gap-3 mb-3">
                <Moon className="text-blue-600" size={28} />
                <h3 className="text-lg font-semibold text-dark-text">🏙️ Trabalhador Urbano</h3>
              </div>
              <p className="text-base text-body-text mb-2">
                Das <strong>22h às 5h</strong> do dia seguinte.
              </p>
              <p className="text-sm text-body-text">
                Adicional mínimo de 20%. (Art. 73, CLT)
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-6 border-2 border-green-200">
              <div className="flex items-center gap-3 mb-3">
                <Tractor className="text-green-600" size={28} />
                <h3 className="text-lg font-semibold text-dark-text">🌾 Rural — Lavoura</h3>
              </div>
              <p className="text-base text-body-text mb-2">
                Das <strong>21h às 5h</strong> do dia seguinte.
              </p>
              <p className="text-sm text-body-text">
                Adicional mínimo de 25%. (Lei 5.889/73)
              </p>
            </div>

            <div className="bg-amber-50 rounded-lg p-6 border-2 border-amber-200">
              <div className="flex items-center gap-3 mb-3">
                <Sun className="text-amber-600" size={28} />
                <h3 className="text-lg font-semibold text-dark-text">🐄 Rural — Pecuária</h3>
              </div>
              <p className="text-base text-body-text mb-2">
                Das <strong>20h às 4h</strong> do dia seguinte.
              </p>
              <p className="text-sm text-body-text">
                Adicional mínimo de 25%. (Lei 5.889/73)
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
            <p className="text-base text-body-text">
              <strong>Hora noturna reduzida:</strong> Para trabalhadores urbanos, a hora noturna não vale 60 minutos — vale apenas <strong>52 minutos e 30 segundos</strong>. Isso significa que quem trabalha das 22h às 5h (7 horas de relógio) recebe como se tivesse trabalhado 8 horas. Essa regra não se aplica aos trabalhadores rurais.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Como Calcular o Adicional Noturno — Passo a Passo
          </h2>
          <p className="text-base text-body-text mb-6">
            O cálculo do <strong>valor do adicional noturno</strong> é simples quando você conhece a fórmula. Veja as etapas detalhadas.
          </p>

          <div className="space-y-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-dark-text mb-2">① Encontre o valor da hora normal</h3>
              <p className="text-base text-body-text">
                Divida o salário bruto pelo divisor da jornada. Para 44 horas semanais, o divisor é 220. A fórmula é: <strong>Salário ÷ 220 = Hora normal</strong>.
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-dark-text mb-2">② Calcule o valor do adicional por hora</h3>
              <p className="text-base text-body-text">
                Multiplique o valor da hora normal pelo percentual do adicional. Para 20%, a fórmula é: <strong>Hora normal × 0,20 = Adicional por hora</strong>.
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-dark-text mb-2">③ Encontre o valor da hora noturna</h3>
              <p className="text-base text-body-text">
                Some o valor da hora normal com o adicional. A fórmula é: <strong>Hora normal + Adicional = Hora noturna</strong>. Ou simplesmente: Hora normal × 1,20.
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-dark-text mb-2">④ Multiplique pelas horas trabalhadas</h3>
              <p className="text-base text-body-text">
                Converta as horas de relógio em horas computadas (multiplique por 60 ÷ 52,5, por causa da hora noturna reduzida; trabalhadores rurais não têm essa redução) e multiplique pelo valor do adicional por hora. O resultado é o total do adicional noturno a receber no período.
              </p>
            </div>
          </div>

          <div className="mt-8 bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-dark-text mb-4">Exemplo prático:</h3>
            <p className="text-base text-body-text mb-3">
              Salário: R$ 2.500,00 | Jornada: 44h/semana | Horas noturnas no mês: 44 (de relógio)
            </p>
            <p className="text-base text-body-text mb-2">Hora normal: R$ 2.500 ÷ 220 = <strong>R$ 11,36</strong></p>
            <p className="text-base text-body-text mb-2">Adicional por hora (20%): R$ 11,36 × 0,20 = <strong>R$ 2,27</strong></p>
            <p className="text-base text-body-text mb-2">Hora noturna total: R$ 11,36 + R$ 2,27 = <strong>R$ 13,64</strong></p>
            <p className="text-base text-body-text mb-2">
              Horas computadas (hora noturna reduzida): 44 × 60 ÷ 52,5 = <strong>50,29 horas</strong>
            </p>
            <p className="text-base text-body-text font-semibold mt-4">
              Total do adicional: R$ 2,2727 × 50,2857 = <strong className="text-blue-primary text-xl">R$ 114,29</strong>
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Tabela do Valor do Adicional Noturno por Faixa Salarial
          </h2>
          <p className="text-base text-body-text mb-6">
            Confira quanto vale o adicional noturno por hora para diferentes faixas de salário, considerando jornada de 44 horas semanais (divisor 220) e percentual de 20%.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="border border-border-gray px-4 py-3 text-left font-semibold text-dark-text">Salário Bruto</th>
                  <th className="border border-border-gray px-4 py-3 text-left font-semibold text-dark-text">Hora Normal</th>
                  <th className="border border-border-gray px-4 py-3 text-left font-semibold text-dark-text">Adicional / Hora</th>
                  <th className="border border-border-gray px-4 py-3 text-left font-semibold text-dark-text">Hora Noturna</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className="border border-border-gray px-4 py-3">R$ 1.621,00 (mínimo 2026)</td><td className="border border-border-gray px-4 py-3">R$ 7,37</td><td className="border border-border-gray px-4 py-3">R$ 1,47</td><td className="border border-border-gray px-4 py-3">R$ 8,84</td></tr>
                <tr className="bg-gray-50"><td className="border border-border-gray px-4 py-3">R$ 2.000,00</td><td className="border border-border-gray px-4 py-3">R$ 9,09</td><td className="border border-border-gray px-4 py-3">R$ 1,82</td><td className="border border-border-gray px-4 py-3">R$ 10,91</td></tr>
                <tr><td className="border border-border-gray px-4 py-3">R$ 2.500,00</td><td className="border border-border-gray px-4 py-3">R$ 11,36</td><td className="border border-border-gray px-4 py-3">R$ 2,27</td><td className="border border-border-gray px-4 py-3">R$ 13,64</td></tr>
                <tr className="bg-gray-50"><td className="border border-border-gray px-4 py-3">R$ 3.000,00</td><td className="border border-border-gray px-4 py-3">R$ 13,64</td><td className="border border-border-gray px-4 py-3">R$ 2,73</td><td className="border border-border-gray px-4 py-3">R$ 16,36</td></tr>
                <tr><td className="border border-border-gray px-4 py-3">R$ 3.500,00</td><td className="border border-border-gray px-4 py-3">R$ 15,91</td><td className="border border-border-gray px-4 py-3">R$ 3,18</td><td className="border border-border-gray px-4 py-3">R$ 19,09</td></tr>
                <tr className="bg-gray-50"><td className="border border-border-gray px-4 py-3">R$ 4.000,00</td><td className="border border-border-gray px-4 py-3">R$ 18,18</td><td className="border border-border-gray px-4 py-3">R$ 3,64</td><td className="border border-border-gray px-4 py-3">R$ 21,82</td></tr>
                <tr><td className="border border-border-gray px-4 py-3">R$ 5.000,00</td><td className="border border-border-gray px-4 py-3">R$ 22,73</td><td className="border border-border-gray px-4 py-3">R$ 4,55</td><td className="border border-border-gray px-4 py-3">R$ 27,27</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-base text-body-text mt-4">
            Esses valores são referências com base no mínimo legal de 20%. Se a convenção coletiva da sua categoria estabelece um percentual maior, use a <a href="#calculadora" className="text-blue-primary hover:underline font-semibold">calculadora acima</a> para obter o valor exato.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Hora Extra Noturna — Como Funciona
          </h2>
          <p className="text-base text-body-text mb-4">
            Quando o trabalhador faz <strong>hora extra durante o período noturno</strong>, os dois adicionais se acumulam. Primeiro, aplica-se o adicional noturno de 20% sobre a hora normal. Depois, aplica-se o adicional de hora extra (50% ou 100%) sobre o valor já acrescido.
          </p>
          
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-dark-text mb-4">Exemplo — Hora extra noturna (50%):</h3>
            <p className="text-base text-body-text mb-2">Salário: R$ 3.000,00 | Divisor: 220</p>
            <p className="text-base text-body-text mb-2">Hora normal: R$ 3.000 ÷ 220 = R$ 13,64</p>
            <p className="text-base text-body-text mb-2">Com adicional noturno (20%): R$ 13,64 × 1,20 = R$ 16,36</p>
            <p className="text-base text-body-text font-semibold">Com hora extra (50%): R$ 16,36 × 1,50 = <strong className="text-blue-primary">R$ 24,55 por hora</strong></p>
          </div>

          <p className="text-base text-body-text mt-4">
            Para calcular horas extras em detalhes, use nossa <Link to="/calculo-de-horas-extraordinarias" className="text-blue-primary hover:underline font-semibold">calculadora de horas extraordinárias</Link>.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Quem Tem Direito ao Adicional Noturno?
          </h2>
          <p className="text-base text-body-text mb-4">
            Praticamente todo trabalhador com carteira assinada que exerça atividades no período noturno tem direito ao adicional. Isso inclui profissionais de saúde, segurança, indústria, comércio, transporte, hotelaria e muitas outras áreas.
          </p>
          <p className="text-base text-body-text mb-4">
            Existem algumas situações específicas que merecem atenção. Trabalhadores em regime de <strong>escala 12×36</strong> também têm direito ao adicional noturno pelas horas trabalhadas entre 22h e 5h. O mesmo vale para empregados em <strong>teletrabalho com controle de jornada</strong> que trabalhem no período noturno.
          </p>
          <p className="text-base text-body-text">
            <strong>Quem não recebe:</strong> Cargos de gestão e confiança (diretores, gerentes) podem ser excluídos do adicional noturno conforme o artigo 62 da CLT. Porém, se houver controle de jornada, o adicional é devido.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            O Adicional Noturno Integra Outros Cálculos?
          </h2>
          <p className="text-base text-body-text mb-4">
            Sim. Quando o adicional noturno é pago com habitualidade (de forma regular, mês após mês), ele integra a base de cálculo de diversos direitos trabalhistas. Isso é importante porque aumenta o valor que você recebe em outras verbas.
          </p>
          <p className="text-base text-body-text mb-4">
            O adicional noturno habitual é considerado no cálculo de férias e terço constitucional, 13º salário, FGTS e multa de 40%, aviso prévio, descanso semanal remunerado (DSR) e horas extras. A Súmula 60 do TST reforça que o adicional noturno pago com habitualidade integra o salário para todos os efeitos legais.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">
            Adicional Noturno na CLT — Principais Regras
          </h2>
          <p className="text-base text-body-text mb-4">
            Veja um resumo das regras mais relevantes sobre o adicional noturno previstas na legislação trabalhista brasileira.
          </p>
          <p className="text-base text-body-text mb-4">
            O artigo 73 da CLT define que o trabalho noturno terá remuneração superior à do diurno, com acréscimo mínimo de 20%. A hora noturna reduzida de 52 minutos e 30 segundos é uma particularidade importante, pois significa que a cada 52min30s trabalhados, o empregado recebe como se tivesse trabalhado 60 minutos. Essa regra cria um efeito prático: quem trabalha 7 horas noturnas no relógio recebe por 8 horas.
          </p>
          <p className="text-base text-body-text">
            A Constituição Federal, no artigo 7º, reforça o adicional noturno como direito fundamental do trabalhador. Nenhuma convenção coletiva pode reduzir o percentual abaixo do mínimo legal.
          </p>
        </section>

        <FAQAccordion faqs={faqs} />

        <RelatedTools 
          tools={[
            { 
              title: 'Calculadora de Horas',
              description: 'Somar e subtrair horas de trabalho',
              link: '/'
            },
            { 
              title: 'Cálculo de Horas Extraordinárias',
              description: 'Calcule hora extra 50%, 75% e 100%',
              link: '/calculo-de-horas-extraordinarias'
            },
            { 
              title: 'Calculadora de Tempo',
              description: 'Somar, subtrair e converter tempo',
              link: '/calculadora-de-tempo'
            },
            { 
              title: 'Calculadora de Horas Extras',
              description: 'Calcule horas extras rapidamente',
              link: '/calculadora-de-horas-extras'
            }
          ]}
        />
      </main>

      <Footer />
    </>
  );
};

export default AdicionalNoturnoValorPage;
